/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 제목 블록을 정의한다.
 *
 * @file /src/rootin/blocks/heading/Heading.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 7.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Paragraph } from '@rootin/blocks/paragraph/Paragraph';
import { Editor } from '@rootin/core/Editor';
import { Text } from '@rootin/elements/text/Text';
import { Dom } from '@utils/Dom';
import { Language, LanguageText } from '@utils/Language';
import { Menu, MenuItems } from '@rootin/core/Menu';
import './Heading.css';

export interface Properties {
    /**
     * 제목 레벨(1~5). 블록 추가 시(메뉴에서 고른 값, 아직 미구현) 확정되고 이후 안 바뀐다.
     */
    level?: number;

    /**
     * 내용이 비어있을 때 표시할 placeholder 텍스트
     */
    placeholder?: LanguageText;
}

export class Heading extends Paragraph {
    /**
     * 블록타입
     */
    static type = 'heading';

    /**
     * 블록아이콘
     */
    static icon =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-newspaper-icon lucide-newspaper"><path d="M15 18h-5"/><path d="M18 14h-8"/><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-4 0v-9a2 2 0 0 1 2-2h2"/><rect width="8" height="4" x="10" y="6" rx="1"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.heading.label');

    /**
     * 메뉴
     */
    static menu: Menu = null;

    /**
     * 제목 레벨(1~6)
     */
    level: number;

    /**
     * 제목 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     * @param properties - 블록설정
     */
    constructor(editor: Editor, properties: Properties = null) {
        const level = properties?.level ?? 1;
        super(editor, { role: `h${level}`, ...properties });
        this.level = level;
    }

    /**
     * 메뉴를 가져온다.
     *
     * @returns 메뉴
     */
    static getMenu(editor: Editor, $button: Dom): Menu {
        if (Heading.menu === null) {
            const levels: number[] = editor.getConfig(this, 'levels') ?? [1, 2, 3, 4, 5, 6];
            const icons: string[] = [
                '',
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-heading1-icon lucide-heading-1"><path d="M4 12h8"/><path d="M4 18V6"/><path d="M12 18V6"/><path d="m17 12 3-2v8"/></svg>',
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-heading2-icon lucide-heading-2"><path d="M4 12h8"/><path d="M4 18V6"/><path d="M12 18V6"/><path d="M21 18h-4c0-4 4-3 4-6 0-1.5-2-2.5-4-1"/></svg>',
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-heading3-icon lucide-heading-3"><path d="M4 12h8"/><path d="M4 18V6"/><path d="M12 18V6"/><path d="M17.5 10.5c1.7-1 3.5 0 3.5 1.5a2 2 0 0 1-2 2"/><path d="M17 17.5c2 1.5 4 .3 4-1.5a2 2 0 0 0-2-2"/></svg>',
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-heading4-icon lucide-heading-4"><path d="M12 18V6"/><path d="M17 10v3a1 1 0 0 0 1 1h3"/><path d="M21 10v8"/><path d="M4 12h8"/><path d="M4 18V6"/></svg>',
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-heading5-icon lucide-heading-5"><path d="M4 12h8"/><path d="M4 18V6"/><path d="M12 18V6"/><path d="M17 13v-3h4"/><path d="M17 17.7c.4.2.8.3 1.3.3 1.5 0 2.7-1.1 2.7-2.5S19.8 13 18.3 13H17"/></svg>',
                '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-heading6-icon lucide-heading-6"><path d="M4 12h8"/><path d="M4 18V6"/><path d="M12 18V6"/><circle cx="19" cy="16" r="2"/><path d="M20 10c-2 2-3 3.5-3 6"/></svg>',
            ];
            const placeholder =
                editor.getConfig(this, 'placeholder') ?? Language.get('rootin.blocks.heading.placeholder');

            const menus: MenuItems = [];
            for (const level of levels) {
                menus.push({
                    icon: icons[level],
                    text: Language.get(`rootin.blocks.heading.heading${level}`),
                    handler: async () => {
                        await editor.insertBlock(this.create(editor, { level, placeholder }));
                        return true;
                    },
                });
            }

            Heading.menu = new Menu({ menus });
            Heading.menu.setTarget($button);
            Heading.menu.$getComponent().on('mousedown', (_, e) => {
                e.preventDefault();
            });
        }

        return Heading.menu;
    }

    /**
     * 블록을 초기화한다.
     * 하위 블록 클래스가 재정의하여 자신이 사용할 툴을 추가하거나, 기타 초기화작업을 수행한다.
     * 최종적으로 에디터의 블록목록에 추가될 버튼 DOM 을 반환한다.
     *
     * @param editor - 블록이 추가되는 에디터
     * @returns 블록추가 버튼 DOM
     */
    static init(editor: Editor): Dom {
        const $button = super.init(editor, this);

        $button.on('mousedown', async ($button, e) => {
            e.preventDefault();

            const menu = Heading.getMenu(editor, $button);
            if (menu.isShow() === true) {
                await menu.hide();
            } else {
                await menu.show(false);
            }
        });

        return $button;
    }

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {
        const dom = document.createElement('div');
        dom.innerHTML = html;

        const $rootinBlock = dom.firstElementChild;
        if (
            $rootinBlock === null ||
            $rootinBlock.tagName !== 'ROOTIN-BLOCK' ||
            $rootinBlock.getAttribute('data-type') !== this.getType()
        ) {
            return;
        }

        const $text = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'text'
        );
        if ($text === undefined) {
            return;
        }

        const level = parseInt(($text.getAttribute('data-role') ?? '').slice(1), 10);
        if (Number.isNaN(level) === false) {
            this.#applyLevel(level);
        }

        this.text.$getElement().html($text.innerHTML);
    }

    /**
     * 선택영역 추출 후 뒤에 남는 내용을 담을, 나와 같은 레벨의 새 제목 블록을 만든다.
     *
     * @returns 새 블록 인스턴스
     */
    protected createRemainBlock(): Paragraph {
        return new Heading(this.editor, { level: this.level, placeholder: this.placeholder });
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        if (type.startsWith('text/') === false) {
            return data;
        }

        if (focused === null) {
            return data;
        }

        const wrappers = [...Text.genericWrapperTags, `rootin-block[data-type='${this.getType()}']`];
        const extractTags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];

        if (focused instanceof Element) {
            return (await this.text.onPaste(type, data, wrappers, extractTags)).remain;
        }

        const container = focused.getParent();
        if (container === null) {
            return data;
        }

        const { remain, matched } = await this.text.onPaste(type, data, wrappers, extractTags, true);
        if (matched === null) {
            if (type === 'text/html') {
                const dom = document.createElement('div');
                dom.innerHTML = data as string;

                const $matched = dom.querySelector(`rootin-block[data-type='${this.getType()}']`);
                if ($matched !== null) {
                    $matched.remove();
                    await this.setValueFromHtml($matched.outerHTML);
                    await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);
                    return dom.innerHTML === '' ? true : dom.innerHTML;
                }
            }

            return data;
        }

        const level = parseInt(matched.slice(1), 10);
        this.#applyLevel(level);

        if (this.text.isEmpty() === false) {
            await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);
        }

        return remain;
    }

    /**
     * 제목 레벨을 적용한다.
     *
     * @param level - 적용할 제목 레벨(1~6)
     */
    #applyLevel(level: number): void {
        this.level = level;
        this.text.properties.role = `h${level}`;
    }
}
